import { Checkbox, type CheckboxProps } from 'antd';
import { type CheckboxChangeEvent } from 'antd/es/checkbox';
import clx from 'classnames';
import { omit } from 'lodash';
import { useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { useField } from '../field/useField.tsx';
import { useController } from '../hooks/useController.ts';
import { useFormItemAccessibility } from '../item/useFormItemAccessibility.tsx';

const StyledCheckboxOption = styled(Checkbox)`
  &.read-only {
    pointer-events: none;
  }
`;

export interface ICheckboxIsolatedInputProps
  extends Omit<CheckboxProps, 'name'> {
  /** Nome del gruppo di checkbox. */
  name: string;
  /** Nome della singola opzione */
  optionName: string;
  /** Valore di questa singola checkbox. */
  optionValue: string;
  /** Label della checkbox. */
  // TODO Non supportato attualmente. Il problema risiede nel fatto che è un AdvancedRichText.
  label?: string;
  /**
   * Aggungiamo proprietà readOnly non gestita da antd
   */
  readOnly?: boolean;
}

/**
 * Renderizza un'opzione singola anche se appartiene
 * logicamente ad un Group (multi-selezione).
 */
export function CheckboxOptionInput(props: ICheckboxIsolatedInputProps) {
  const { name, optionName, optionValue, readOnly, className, ...inputProps } =
    props;

  // NOTE: non usiamo `useFormItemEnabled` perché viene già passato dal padre
  // quando richiamamo questo componente.
  // const { visuallyDisabled: parentVisuallyDisabled } = useFormItemEnabled();

  // Informazioni del gruppo
  const group = useField(name);

  // Informazioni del singolo campo
  const { enabled, visible, unregister } = useField(optionName);
  const { field } = useController(optionName);

  // Il reset del valore su disabilitazione è gestito automaticamente
  // dallo schema Yup (setOptionEnabledCondition → transform → getDefault)
  // tramite syncComputedValues.

  useEffect(() => {
    if (visible) return;
    unregister();
  }, [unregister, visible]);

  const handleChange = useCallback(
    (e: CheckboxChangeEvent) => {
      field.onChange(e.target.checked);
      // Simuliamo il blur al change
      field.onBlur();
    },
    [field],
  );

  // TODO Non mi sembra corretto perché qui gestiamo la singola opzione.
  // es. il `label Id` è un altro
  const accessibility = useFormItemAccessibility();

  if (!visible) return null;

  return (
    <StyledCheckboxOption
      {...omit(accessibility.attributes, 'id')}
      {...inputProps}
      disabled={
        // parentVisuallyDisabled ||
        group.enabled === false || enabled === false || inputProps.disabled
      }
      ref={field.ref}
      title={props.label}
      checked={!!field.value}
      onChange={readOnly ? undefined : handleChange}
      onClick={readOnly ? undefined : inputProps.onClick}
      className={clx(className, { 'read-only': readOnly })}
    />
  );
}
